import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from '@mui/material';
import { ApprovalService } from '../../services/approval.service';
import { ApprovalRequestDetail } from '../../interfaces/approval.interface';
import ApprovalStatusCard from '../approvals/ApprovalStatusCard';
import { useToast } from '../../context/ToastContext';

interface Props {
  quotationId: string;
  approvalStatus?: string | null;
  approvalNote?: string | null;
  /** Called after an approver decided, so the quotation reloads. */
  onDecided: () => void;
}

/**
 * S1: the approval state of a quotation, with approve / reject for the one person who may decide it. The server decides who
 * that is (canDecide): the quote's team manager or department head inside their own scope, never the maker.
 */
const QuotationApprovalPanel: React.FC<Props> = ({ quotationId, approvalStatus, approvalNote, onDecided }) => {
  const toast = useToast();
  const [detail, setDetail] = useState<ApprovalRequestDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [comment, setComment] = useState('');
  const status = approvalStatus ?? 'NONE';

  useEffect(() => {
    if (status !== 'PENDING') { setDetail(null); return; }
    let live = true;
    Promise.resolve(ApprovalService.forSubject('QUOTATION', quotationId))
      .then(r => { if (live) setDetail(r?.request ?? null); })
      .catch(() => { if (live) setDetail(null); });
    return () => { live = false; };
  }, [quotationId, status]);

  const decide = async (approve: boolean) => {
    if (!detail) return;
    setBusy(true);
    try {
      if (approve) await ApprovalService.approve(detail.request.id, undefined, detail.request.currentSeq);
      else await ApprovalService.reject(detail.request.id, comment, detail.request.currentSeq);
      toast.showSuccess(approve ? 'Discount approved' : 'Quotation returned to draft');
      setRejectOpen(false);
      setComment('');
      onDecided();
    } catch (e) {
      toast.showApiError(e, approve ? 'Failed to approve' : 'Failed to reject');
    } finally {
      setBusy(false);
    }
  };

  if (status === 'NONE') return null;
  return (
    <Box sx={{ mb: 2 }} data-testid="quotation-approval-panel" data-help-section="sales.quotation.approval">
      {status === 'PENDING' && <Alert severity="warning" sx={{ mb: 1 }}>This quotation waits for a discount approval before it can be sent.</Alert>}
      {status === 'APPROVED' && <Alert severity="success" sx={{ mb: 1 }}>The discount is approved. You can send the quotation.</Alert>}
      {status === 'REJECTED' && (
        <Alert severity="error" sx={{ mb: 1 }}>Discount rejected{approvalNote ? `: ${approvalNote}` : ''}. Edit the quotation, then send it again.</Alert>
      )}
      <ApprovalStatusCard subjectType="QUOTATION" subjectId={quotationId} refreshKey={status} />
      {status === 'PENDING' && detail?.canDecide && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button data-help="sales.quotation.approveDiscount" variant="contained" color="success" disabled={busy} onClick={() => decide(true)}>
            {busy ? <CircularProgress size={20} /> : 'Approve discount'}
          </Button>
          <Button data-help="sales.quotation.rejectDiscount" variant="outlined" color="error" disabled={busy} onClick={() => setRejectOpen(true)}>Reject discount</Button>
        </Box>
      )}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="sm" data-help-page="sales.quotation.rejectDialog.page">
        <DialogTitle>Reject this discount?</DialogTitle>
        <DialogContent data-help-section="sales.quotation.rejectDialog.section.note">
          <TextField data-help="sales.quotation.rejectDialog.note" label="Note for the salesperson" value={comment} onChange={e => setComment(e.target.value)}
            fullWidth multiline minRows={2} required sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.quotation.rejectDialog.cancel" onClick={() => setRejectOpen(false)} disabled={busy}>Cancel</Button>
          <Button data-help="sales.quotation.rejectDialog.confirm" variant="contained" color="error" disabled={busy || comment.trim().length === 0} onClick={() => decide(false)}>
            Confirm reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuotationApprovalPanel;
